import { NotificationType } from "@cronika/proto/common/notification_pb";
import { ROUTES, withLocale } from "@/shared/config/routes";
import type { AppNotification } from "@/shared/stores/NotificationsStore";
import { resolveMethodName } from "@/features/wallet/utils/payment-method-name";

/** Display shape a notification is rendered from, regardless of its kind. */
export interface NotificationView {
  title: string;
  body: string;
  action?: { label: string; href: string };
}

/** Minimal translate signature matching `@cronika/locales` useTranslation. */
export type Translate = (key: string, params?: Record<string, string | number>) => string;

type Presenter = (n: AppNotification, t: Translate, locale: string) => NotificationView;

const BRAND = "Cronika";

// ── DEPOSIT_SUCCEEDED ──────────────────────────────────────────────────────
interface DepositPayload {
  amount?: string;
  currency?: string;
  method?: string | null;
  nameTranslations?: Array<{ locale: string; name: string }>;
  displayId?: string;
}

const depositSucceeded: Presenter = (n, t, locale) => {
  const p = n.payload as DepositPayload;
  const amount = p.amount ? parseFloat(p.amount).toFixed(2) : "—";
  const method = resolveMethodName(p.nameTranslations, locale) ?? p.method ?? "—";
  return {
    title: BRAND,
    body: `${t("notifications.deposit-body", { method, amount })} ${t("notifications.deposit-id", { orderId: p.displayId ?? "" })}`,
    action: p.displayId
      ? { label: t("notifications.more-details"), href: `${withLocale(ROUTES.WALLET)}?tab=history&tx=${p.displayId}` }
      : undefined,
  };
};

// ── Fallback for unknown / not-yet-supported kinds ─────────────────────────
const fallback: Presenter = (_n, t, _locale) => ({
  title: BRAND,
  body: t("notifications.generic-body", { defaultValue: "You have a new notification." }),
});

// Registry: add a new kind here (+ a payload type + locale keys) — nothing else
// in the store or modal needs to change.
const PRESENTERS: Partial<Record<NotificationType, Presenter>> = {
  [NotificationType.DEPOSIT_SUCCEEDED]: depositSucceeded,
};

export const presentNotification = (n: AppNotification, t: Translate, locale: string): NotificationView =>
  (PRESENTERS[n.type] ?? fallback)(n, t, locale);